/**
 * The one way to ask for the offline sync queue to be re-read from Rust.
 *
 * The queue atom has a single owner — the offline-recording event subscriber —
 * and re-reading it is not just `list()` plus `setQueue`. The owner folds each
 * snapshot through a reconcile that carries in-memory `synced` rows Rust has
 * already deleted, refuses rows for recordings this renderer has seen finish,
 * and drops replies older than the last one applied. Several `list()` calls can
 * be in flight at once on a batch reconnect, and nothing orders their replies.
 *
 * A second writer that skips any of that can rewind rows the owner just wrote,
 * resurrect a finished recording at an in-flight status, or blink a synthetic
 * session-list row out. So other surfaces do not write the queue: they ask for a
 * refresh here, and the owner performs it under its own coordination.
 *
 * Registration rather than a direct import because the coordination state is
 * per-subscription and lives in the owner's effect. Nothing is registered before
 * the subscriber mounts or after it unmounts, and a request in that window is a
 * no-op by design: there is no queue owner to coordinate with, and the
 * subscriber re-reads the durable list on mount anyway.
 */
type OfflineSyncQueueRefresh = (reason: string) => Promise<void>;

/**
 * The reason the note body's Retry passes. The queue owner keys its one
 * rejecting refresh off this exact value, so both sides share the literal.
 */
export const NOTE_UNAVAILABLE_RETRY_REFRESH_REASON = 'note_unavailable_retry';

let activeRefresh: OfflineSyncQueueRefresh | null = null;

/**
 * Publish the coordinated refresh. Returns the matching unregister, which only
 * clears the slot if this registration is still the active one, so a remount
 * that registers before the old cleanup runs cannot leave the slot empty.
 */
export function registerOfflineSyncQueueRefresh(
  refresh: OfflineSyncQueueRefresh
): () => void {
  activeRefresh = refresh;
  return () => {
    if (activeRefresh === refresh) activeRefresh = null;
  };
}

/**
 * Ask the queue owner to re-read the durable list. Resolves once the refresh has
 * been applied, so a caller can wait for the queue to reflect what it just asked
 * Rust to do. Resolves immediately when nothing is registered.
 *
 * `reason` is carried for the owner's own diagnostics on a failed read.
 */
export async function requestOfflineSyncQueueRefresh(reason: string): Promise<void> {
  await activeRefresh?.(reason);
}

/** Test seam: drop any registration so a suite cannot leak into the next. */
export function resetOfflineSyncQueueRefreshForTests(): void {
  activeRefresh = null;
}
